import PropTypes from 'prop-types';
import { CompositeDecorator, Editor, EditorState } from 'draft-js';
import {
  forwardRef,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from '@web-stories-wp/react';
import { FONT_REFERENCE, FONT_SIZE, LINE_HEIGHT } from './customConstants';

const MAX_PROJECTED_CHARACTERS = 2000;
const LINE_TOP_TOLERANCE = 0.5;

export function canProjectCaretContent(content) {
  return (
    content.getPlainText().length <= MAX_PROJECTED_CHARACTERS &&
    content
      .getBlocksAsArray()
      .every(
        (block) =>
          block.getType() === 'unstyled' &&
          !block.getText().includes('\n') &&
          !block
            .getCharacterList()
            .some((character) =>
              character
                .getStyle()
                .some((style) =>
                  [FONT_REFERENCE, FONT_SIZE, LINE_HEIGHT].some((prefix) =>
                    style.startsWith(`${prefix}-`)
                  )
                )
            )
      )
  );
}

export function canUseScreenSpaceText(editorState, scale) {
  return (
    Number.isFinite(scale) &&
    scale > 0 &&
    scale < 1 &&
    !editorState.getDecorator() &&
    !editorState.getDirectionMap().some((direction) => direction === 'RTL') &&
    canProjectCaretContent(editorState.getCurrentContent())
  );
}

// A view-only range: Draft's actual content and selection offsets stay intact.
function ScreenLine({ children, start }) {
  return (
    <span
      data-native-caret-line={start}
      style={{ display: 'block', whiteSpace: 'pre', position: 'relative' }}
    >
      {children}
    </span>
  );
}
ScreenLine.propTypes = { children: PropTypes.node, start: PropTypes.number };

export function createLineDecorator(lines) {
  return new CompositeDecorator([
    {
      strategy: (block, callback) => {
        (lines[block.getKey()] || []).forEach(({ start, end }) => {
          if (end > start) {
            callback(start, end);
          }
        });
      },
      component: ScreenLine,
    },
  ]);
}

export function groupCharacterLines(
  characters,
  tolerance = LINE_TOP_TOLERANCE
) {
  const lines = [];
  characters.forEach(({ offset, top, left }) => {
    const last = lines[lines.length - 1];
    if (!last || Math.abs(last.top - top) > tolerance) {
      lines.push({ start: offset, end: offset + 1, top, left });
    } else {
      last.end = offset + 1;
    }
  });
  return lines;
}

export function measureCanonicalLayout(root, scale) {
  const lines = {};
  const blocks = {};
  let complete = true;
  const rootRect = root.getBoundingClientRect();
  root.querySelectorAll('[data-block="true"]').forEach((block) => {
    const key = block.getAttribute('data-offset-key').split('-')[0];
    const blockRect = block.getBoundingClientRect();
    blocks[key] = {
      left: (blockRect.left - rootRect.left) / scale,
      top: (blockRect.top - rootRect.top) / scale,
    };
    const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
    const range = document.createRange();
    const characters = [];
    let node;
    let offset = 0;
    while ((node = walker.nextNode())) {
      for (let index = 0; index < node.length; index++, offset++) {
        range.setStart(node, index);
        range.setEnd(node, index + 1);
        const rect = range.getClientRects()[0];
        if (rect && rect.height > 0) {
          characters.push({
            offset,
            top: (rect.top - blockRect.top) / scale,
            left: (rect.left - blockRect.left) / scale,
          });
        } else {
          complete = false;
        }
      }
    }
    lines[key] = groupCharacterLines(characters);
  });
  return complete && Object.keys(blocks).length ? { lines, blocks } : null;
}

function firstCharacterRect(element) {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  let node;
  while ((node = walker.nextNode())) {
    if (node.length) {
      range.setStart(node, 0);
      range.setEnd(node, 1);
      const rect = range.getClientRects()[0];
      if (rect) {
        return rect;
      }
    }
  }
  return null;
}

// Translate whole lines, never individual glyphs or the caret. All rectangles
// are read before writing offsets. Existing offsets are included so repeated
// measurement is idempotent and zoom/font changes do not accumulate drift.
export function alignProjectedLines(root, layout, scale) {
  const rootRect = root.getBoundingClientRect();
  const updates = [];
  const align = (element, target, current) => {
    updates.push({
      element,
      left:
        (parseFloat(element.style.left) || 0) +
        target.left * scale -
        current.left,
      top:
        (parseFloat(element.style.top) || 0) + target.top * scale - current.top,
    });
  };
  root.querySelectorAll('[data-block="true"]').forEach((block) => {
    const key = block.getAttribute('data-offset-key').split('-')[0];
    const target = layout.blocks[key];
    if (!target) {
      return;
    }
    const blockRect = block.getBoundingClientRect();
    // Also preserves the origin of empty blocks, which have no decorated range.
    align(block, target, {
      left: blockRect.left - rootRect.left,
      top: blockRect.top - rootRect.top,
    });
    const linesByStart = new Map(
      (layout.lines[key] || []).map((line) => [line.start, line])
    );
    block.querySelectorAll('[data-native-caret-line]').forEach((line) => {
      const start = Number(line.getAttribute('data-native-caret-line'));
      const canonicalLine = linesByStart.get(start);
      const rect = firstCharacterRect(line);
      if (canonicalLine && rect) {
        align(line, canonicalLine, {
          left: rect.left - blockRect.left,
          top: rect.top - blockRect.top,
        });
      }
    });
  });
  updates.forEach(({ element, left, top }) => {
    element.style.position = 'relative';
    element.style.left = `${left}px`;
    element.style.top = `${top}px`;
  });
}

export function scalePixelStyle(style, scale) {
  const scaled = { ...style };
  ['fontSize', 'lineHeight', 'letterSpacing'].forEach((property) => {
    const value = style[property];
    if (typeof value === 'string' && value.endsWith('px')) {
      scaled[property] = `${parseFloat(value) * scale}px`;
    }
  });
  return scaled;
}

export function restoreEditorDecorator(editorState, decorator) {
  return editorState.getDecorator() === decorator
    ? editorState
    : EditorState.set(editorState, { decorator });
}

export function createMeasurementEditorState(editorState) {
  const selection = editorState.getSelection();
  return selection.getHasFocus()
    ? EditorState.acceptSelection(editorState, selection.set('hasFocus', false))
    : editorState;
}

/**
 * Keeps canonical measurement in normal flow, but paints the one editable Draft
 * DOM in screen coordinates. Its inverse transform cancels NativeTextLayout's
 * scale; font metrics are scaled instead. Limited to uniform unrotated text.
 * Measurement is bounded; unsupported layouts keep the normal native editor.
 * The editable Draft instance stays mounted across both rendering paths.
 */
function ScreenSpaceTextEditor({ scale, ...editorProps }, ref) {
  const canonicalRef = useRef(null);
  const screenRef = useRef(null);
  const committedProjection = useRef({ active: false, scale, layout: null });
  const [layout, setLayout] = useState(null);
  const { editorState } = editorProps;
  const composing = editorState.isInCompositionMode();
  const eligible = canUseScreenSpaceText(editorState, scale);
  const canMeasure = eligible && !composing;

  const measure = useCallback(() => {
    if (!canMeasure) {
      return;
    }
    const root = canonicalRef.current;
    if (!root) {
      return;
    }
    const computed = getComputedStyle(root);
    const width = parseFloat(computed.width);
    const height = parseFloat(computed.height);
    // A missing/hidden layout must never leave an unmeasured editable surface.
    if (!(width > 0 && height > 0 && parseFloat(computed.fontSize) > 0)) {
      setLayout(null);
      return;
    }
    // Near-zero line advances can be indistinguishable within the grouping
    // tolerance. Keep a rounding margin and let native layout preserve their
    // soft wraps instead of joining them into a non-wrapping projected line.
    if (parseFloat(computed.lineHeight) <= 2 * LINE_TOP_TOLERANCE) {
      setLayout(null);
      return;
    }
    const measured = measureCanonicalLayout(root, scale);
    if (!measured) {
      setLayout(null);
      return;
    }
    const next = {
      ...measured,
      style: {
        width: `${width * scale}px`,
        height: `${height * scale}px`,
        ...scalePixelStyle(
          {
            fontSize: computed.fontSize,
            lineHeight: computed.lineHeight,
            letterSpacing: computed.letterSpacing,
          },
          scale
        ),
      },
    };
    setLayout((previous) =>
      JSON.stringify(previous) === JSON.stringify(next) ? previous : next
    );
  }, [canMeasure, scale]);

  // Inherited CSS (e.g. Studio's text alignment) can change without resizing
  // the canonical box or changing Draft content. Check each committed render.
  useLayoutEffect(() => {
    measure();
  });

  useLayoutEffect(() => {
    if (!canMeasure || !canonicalRef.current) {
      return undefined;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(canonicalRef.current);
    document.fonts?.addEventListener('loadingdone', measure);
    return () => {
      observer.disconnect();
      document.fonts?.removeEventListener('loadingdone', measure);
    };
  }, [canMeasure, measure]);

  // Draft owns the DOM throughout IME composition. Freeze the projection until
  // it commits rather than changing decorators, geometry or the editable node.
  const projection = composing
    ? committedProjection.current
    : { active: eligible && Boolean(layout), layout, scale };
  const active = projection.active;

  const decorator = useMemo(
    () =>
      projection.layout ? createLineDecorator(projection.layout.lines) : null,
    [projection.layout]
  );
  const projectedState = useMemo(
    () => (active ? EditorState.set(editorState, { decorator }) : editorState),
    [active, decorator, editorState]
  );
  const measurementState = useMemo(
    () => createMeasurementEditorState(editorState),
    [editorState]
  );
  const withoutProjectionDecorator = useCallback(
    (state) => restoreEditorDecorator(state, editorState.getDecorator()),
    [editorState]
  );

  // Draft can update its DOM for selection as well as content. Re-align before
  // paint without changing its selection, content, undo stack or native caret.
  useLayoutEffect(() => {
    if (composing) {
      return;
    }
    committedProjection.current = projection;
    if (active && screenRef.current) {
      alignProjectedLines(
        screenRef.current,
        projection.layout,
        projection.scale
      );
    } else {
      // Relative block offsets were imperative view-only writes. Clear them when
      // returning to canonical rendering without remounting the focused editor.
      screenRef.current
        ?.querySelectorAll('[data-block="true"]')
        .forEach((block) => {
          block.style.removeProperty('left');
          block.style.removeProperty('top');
          block.style.removeProperty('position');
        });
    }
  });

  return (
    <div
      data-testid="screen-space-text-editor"
      style={{ position: 'relative' }}
    >
      {(eligible || active) && (
        <div
          key="canonical"
          ref={canonicalRef}
          aria-hidden="true"
          data-testid="canonical-caret-measurement"
          style={{
            visibility: 'hidden',
            pointerEvents: 'none',
            ...(active
              ? null
              : { position: 'absolute', top: 0, left: 0, width: '100%' }),
          }}
        >
          <Editor
            editorState={measurementState}
            customStyleFn={editorProps.customStyleFn}
            blockStyleFn={editorProps.blockStyleFn}
            textAlignment={editorProps.textAlignment}
            readOnly
          />
        </div>
      )}
      <div
        key="editable"
        ref={screenRef}
        data-testid={
          active ? 'screen-space-caret-surface' : 'native-caret-surface'
        }
        style={
          active
            ? {
                position: 'absolute',
                top: 0,
                left: 0,
                transformOrigin: 'top left',
                transform: `scale(${1 / projection.scale})`,
                ...projection.layout.style,
              }
            : undefined
        }
      >
        <Editor
          {...editorProps}
          ref={ref}
          editorState={projectedState}
          customStyleFn={(styles, block) =>
            active
              ? scalePixelStyle(
                  editorProps.customStyleFn(styles, block),
                  projection.scale
                )
              : editorProps.customStyleFn(styles, block)
          }
          handleBeforeInput={(chars, state, eventTimeStamp) =>
            editorProps.handleBeforeInput?.(
              chars,
              withoutProjectionDecorator(state),
              eventTimeStamp
            )
          }
          handleKeyCommand={(command, state, eventTimeStamp) =>
            editorProps.handleKeyCommand?.(
              command,
              withoutProjectionDecorator(state),
              eventTimeStamp
            )
          }
          handlePastedText={(text, html, state) =>
            editorProps.handlePastedText?.(
              text,
              html,
              withoutProjectionDecorator(state)
            )
          }
          handleReturn={(event, state) =>
            editorProps.handleReturn?.(event, withoutProjectionDecorator(state))
          }
          onChange={(next) =>
            editorProps.onChange(withoutProjectionDecorator(next))
          }
        />
      </div>
    </div>
  );
}

const ScreenSpaceTextEditorWithRef = forwardRef(ScreenSpaceTextEditor);

ScreenSpaceTextEditor.propTypes = {
  scale: PropTypes.number,
  editorState: PropTypes.object.isRequired,
  customStyleFn: PropTypes.func.isRequired,
  blockStyleFn: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  textAlignment: PropTypes.oneOf(['left', 'center', 'right']),
};

ScreenSpaceTextEditorWithRef.propTypes = ScreenSpaceTextEditor.propTypes;
export default ScreenSpaceTextEditorWithRef;
